import React from "react";
import { Lead, Section, P, Bullets, Callout, Img, CTA } from "../../../../blog/components";
import shotThreePrompts from "../../../../assets/blog/dashboard-three-prompts.jpg";
import shotDefault from "../../../../assets/blog/dashboard-default-llm.jpg";

export const title = "Why LLMs can’t design dashboards";
export const excerpt =
  "Ask any model for a dashboard and you get the same dashboard: four cards, a line chart, a donut, a table. The code is excellent. The design is empty. An opinion on why that keeps happening.";
export const heroAlt = "Moebius-style plain stretching flat to the horizon";
export const readingTime = "8 min read";

export default function Post() {
  return (
    <>
      <Lead>
        Models write beautiful dashboard code. They do not design dashboards. Those are different
        skills, and only one of them is in the training data.
      </Lead>

      <P>
        Try it yourself. Ask for an analytics dashboard, a SaaS admin panel, a view for a payments
        team. Different products, different users, different decisions. You will get the same page
        back, three times, with the accent color rotated.
      </P>

      <Img
        src={shotThreePrompts}
        alt="Three dashboard layouts from three different prompts, identical in structure"
        caption="Three prompts, one layout. Reconstructions of the default output, drawn for this post."
      />

      <P>
        Sidebar with six or seven items. Four metric cards across the top. A wide area chart with a
        gradient fill. A donut next to it. A table of recent activity at the bottom. Every card the
        same size, every number the same weight, every delta green and pointing up.
      </P>

      <Section title="The default dashboard">
        <P>
          Here is the full version of it, rendered from a one-shot prompt with no further direction.
          Nothing about it is broken. The spacing is consistent, the type scale is sane, the contrast
          passes, the components are composable. As a piece of frontend engineering it is better than
          most things shipped by hand.
        </P>

        <Img
          src={shotDefault}
          alt="A generic AI-generated dashboard: purple gradient, four identical metric cards, area chart, donut chart, recent sales table"
          caption="The shape you get by default. Note that it fits on one screen and tells you nothing."
        />

        <P>
          Now try to use it. Is the business fine this morning? Should anyone do something? Which of
          those four numbers would you wake a person up for? The interface has no opinion. It gives
          you revenue, subscriptions, sales and active users at exactly equal visual weight, because
          it does not know which one matters, and it has no way to find out.
        </P>
        <P>
          If those numbers look oddly familiar, they should. “$45,231.89, up 20.1% from last month”
          and “Olivia Martin, olivia.martin@email.com” come from a widely copied component library
          demo. The model is not inventing a dashboard. It is reciting one.
        </P>
      </Section>

      <Section title="The code is not the problem">
        <P>
          It is tempting to file this under “AI is bad at CSS”. It is not. The failure sits one level
          above the code, in the part of design that happens before anything is rendered:
        </P>
        <Bullets
          items={[
            "Deciding what this screen is for, and what it is not for.",
            "Deciding which single number gets to be the biggest thing on it.",
            "Deciding what gets cut so the important thing has room.",
            "Deciding what the reader should do when the number is bad.",
          ]}
        />
        <P>
          Those are not styling choices. They are claims about the business, and a dashboard is
          nothing but the sum of those claims, expressed in font sizes. Every dashboard is an
          argument about what matters. A model with no stake in the outcome has no argument to make,
          so it makes the only layout that requires no position: everything, equally.
        </P>
        <Callout>
          Equal weight is the null hypothesis of layout. It is what you produce when you have not
          decided anything yet.
        </Callout>
      </Section>

      <Section title="Why it could be this way">
        <P>
          I have four theories. None of them is “the models are not smart enough yet”, which I think
          is the least interesting explanation available.
        </P>
      </Section>

      <Section title="1. It learned the picture, not the job">
        <P>
          Real operational dashboards live behind a login. The ones a support lead stares at during
          an incident, the ones a warehouse manager keeps on a wall screen, the ones a trading desk
          actually reads. None of that is on the public web, and none of it is in the training data.
        </P>
        <P>What is public: portfolio shots, template marketplaces, component library demos, tutorial
          repos, design inspiration galleries. Every one of those artifacts is optimized to look good
          as a thumbnail to a person who will never use it. That is a completely different objective
          function than being readable at 3am by someone who already knows the system.
        </P>
        <P>
          So the model learned the genre conventions of dashboard imagery with near perfect fidelity.
          It just learned them from marketing material. Ask it for a dashboard and it gives you a
          picture of a dashboard, which is exactly what it was shown.
        </P>
      </Section>

      <Section title="2. Design is subtraction, and subtraction is unrewarded">
        <P>
          Good operational screens are mostly the result of removals. You cut the metric nobody acted
          on, the chart that was only there because the data existed, the third filter, the vanity
          number the founder asked for in 2023. What survives is small and dense and slightly ugly,
          and it works.
        </P>
        <P>
          Now think about what removal looks like from a model’s position. Adding a card is free and
          reads as helpful. Removing one is a bet that the user did not want it, and if the bet is
          wrong, the response looks lazy or incomplete. Training that rewards helpfulness rewards
          coverage. Coverage means more cards.
        </P>
        <P>
          The result is a system that is structurally additive being asked to perform a structurally
          subtractive craft. It hedges, and hedging in layout looks exactly like a four-by-three grid
          of equally sized cards.
        </P>
      </Section>

      <Section title="3. It has no stake, so it cannot rank">
        <P>
          Hierarchy requires consequences. You make one number huge because being late on that number
          costs money or wakes someone up. That knowledge does not live in the prompt and usually does
          not live in the codebase either. It lives in the operator’s head, in what broke last quarter,
          in which VP asks which question on Monday.
        </P>
        <P>
          A model asked to design a dashboard is being asked to rank things whose consequences it
          cannot observe. Refusing to rank is, honestly, the correct behavior under that uncertainty.
          The problem is that we read the refusal as a design, because it arrives looking like one.
        </P>
      </Section>

      <Section title="4. Resemblance is the wrong target">
        <P>
          The obvious objection: models can look at images now. Show the output back to a multimodal
          model and it will happily critique it.
        </P>
        <P>
          It will, and the critique will be about resemblance. Does this look like a professional
          dashboard? Yes. Is the spacing consistent? Yes. Ship it. What it cannot do is the thing a
          designer does when they look at their own screen and feel a small physical discomfort,
          because they know the person using it has ninety seconds and the answer is not on the page.
          That discomfort comes from having watched someone use the thing. It is not a visual
          property, so no amount of looking recovers it.
        </P>
      </Section>

      <Section title="The screenshot I owe you, and cannot give you">
        <P>
          This is the point in the essay where the after shot goes. Same data, done properly, so you
          can see the difference. I built one. It is not here, and the reason it is not here is the
          argument.
        </P>
        <P>
          It was rendered by the same machine that produced the first screenshot, this time from my
          ranking instead of its own: one enormous failure count, a spike marked at the deploy that
          caused it, the table sorted by lost revenue, a rollback suggested where the donut used to
          be. It looked more serious than the default. Held up next to a screen that people actually
          get paged to, it was still a picture of a dashboard, and putting it here would have been
          exactly the move this essay is complaining about: an interface optimized to be looked at by
          someone who will never use it.
        </P>
        <P>
          Because what makes an operational screen good is not visible in a screenshot. It is residue
          of use. It is the column someone demanded back the week after an outage, the metric that got
          cut when it turned out nobody had ever acted on it, the threshold that moved three times
          before it stopped waking people at 4am, the row order that only makes sense if you know
          which vendor lies on its status page. None of that is a visual property. It cannot be
          rendered, and I cannot fake it convincingly enough to be worth your time.
        </P>
        <P>
          That is also why the dashboards that photograph best are the ones nobody has used yet, which
          is the training-data problem from earlier arriving on my own doorstep. So take the
          description instead of the picture: the screen is for the person on call, it says in one
          glance whether checkout is broken, and it says what to do about it. Whether it is good is
          not decided by how it looks here. It is decided at 4am, by someone who did not choose to be
          awake.
        </P>
      </Section>

      <Section title="Which leaves a renderer, not a designer">
        <P>
          Note what did happen in that attempt, though. A model rendered the screen. A model did not
          design it. Not one of those decisions was proposed by it: not which number gets to be
          enormous, not that the spike needed the deploy marked on it, not that the donut had to go,
          not that a rollback outranks four more metrics. Every one was handed over, and it took
          several rounds of handing them over. Left to its own judgment at any step it reverts,
          proposing another card, another chart because the data exists, a green delta on a number
          nobody acts on.
        </P>
        <P>
          So this is not a complaint about the technology. It is a claim about where the value moved.
          Rendering is solved, and that is genuinely worth a lot. What did not move is judgment about
          what matters, and no amount of model quality will move it, because the input simply is not
          in the machine.
        </P>
        <P>
          The practical form of that judgment is small and boring, which is why it is so easy to skip:
        </P>
        <Bullets
          items={[
            "Say who reads this screen and at what moment.",
            "Say which single number decides whether they act.",
            "Say what they do next, and put that on the screen too.",
            "Say what to cut, explicitly, because it will not cut anything on its own.",
          ]}
        />
        <P>
          Give a model that and it will build the thing quickly, in any framework you like, and save
          you a real day of work. Give it nothing and it produces the average of every dashboard
          screenshot on the internet: a real thing it has learned to make, and a useless thing to look
          at. What it will never do is notice that you forgot to decide.
        </P>
        <P>
          The uncomfortable part is that this holds for most agent work, not just dashboards. The
          bottleneck was never the typing.
        </P>
      </Section>

      <Callout>
        Every dashboard is someone saying “this is what matters”. If nobody said it, the screen will
        say nothing, no matter how well it is built.
      </Callout>

      <CTA label="Meet CATE" href="/" />
    </>
  );
}
